import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { CalendarDays, Plus, Sparkles, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePanel } from "@/context/PanelContext";
import { formatTime } from "@/lib/utils-tg";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import CompanyBadge from "@/components/CompanyBadge";

function groupByDay(meetings) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  for (const m of meetings) {
    const d = (m.starts_at || "").slice(0, 10);
    if (d === today) groups.Today.push(m);
    else if (d === yesterday) groups.Yesterday.push(m);
    else groups.Earlier.push(m);
  }
  return groups;
}

export default function MeetingsPage() {
  const { openNew } = useOutletContext();
  const { open: openPanel, refreshKey, triggerRefresh } = usePanel();
  const confirm = useConfirm();
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    api.get("/meetings").then((r) => setMeetings(r.data));
  }, [refreshKey]);

  const groups = groupByDay(meetings);

  const openMeeting = async (id) => {
    const r = await api.get(`/meetings/${id}`);
    openPanel("meeting", r.data);
  };

  const editMeeting = async (id) => {
    // Open the right panel directly on the Details tab where edit lives
    const r = await api.get(`/meetings/${id}`);
    openPanel("meeting", { ...r.data, __initialTab: "details", __startEditing: true });
  };

  const deleteMeeting = async (m) => {
    const ok = await confirm({
      title: "Delete meeting?",
      description: `"${m.title}" and its notes will be permanently removed.`,
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Meeting deleted");
      triggerRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-6 md:px-10 pt-6 max-w-5xl mx-auto" data-testid="meetings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Meetings</h1>
          <p className="text-slate-500 mt-1">Add notes and let AI extract tasks, decisions, and a summary.</p>
        </div>
        <button onClick={openNew} className="tg-btn-primary" data-testid="meetings-new">
          <Plus className="h-4 w-4" /> New meeting
        </button>
      </div>

      {Object.entries(groups).map(([label, items]) =>
        items.length === 0 ? null : (
          <div key={label} className="mb-6">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 px-1">{label}</div>
            <div className="tg-card divide-y divide-slate-100">
              {items.map((m) => (
                <div
                  key={m.id}
                  className="group w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors first:rounded-t-2xl last:rounded-b-2xl"
                  data-testid={`meeting-row-${m.id}`}
                >
                  <button
                    onClick={() => openMeeting(m.id)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    data-testid={`meeting-open-${m.id}`}
                  >
                    <div className="tg-stat-icon bg-emerald-50">
                      <CalendarDays className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{m.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {formatTime(m.starts_at)} · {m.duration_minutes ?? 30}m
                        {m.participants?.length ? ` · ${m.participants.length} participants` : ""}
                      </div>
                    </div>
                    <CompanyBadge entry={m} />
                    {m.ai_summary && (
                      <span className="tg-pill bg-emerald-50 text-emerald-700 border-emerald-200">
                        <Sparkles className="h-3 w-3" /> AI
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => editMeeting(m.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 flex items-center justify-center"
                    title="Edit meeting"
                    data-testid={`meeting-edit-${m.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteMeeting(m)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center"
                    title="Delete meeting"
                    data-testid={`meeting-delete-${m.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {meetings.length === 0 && (
        <div className="tg-card p-8 text-center text-slate-400 text-sm">No meetings yet.</div>
      )}
    </div>
  );
}
